import React, { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import AccordionTitleIcon from "../common/Icons/AccordionTitleIcon";
import { useTrackContext } from "../../hooks/useTrackContext";

interface Section {
  title: string;
  content: React.ReactNode;
}

interface ModalWithAccordionProps {
  title: string;
  sections: Section[];
  showFAQIcon?: boolean;
  iconWidth?: number;
  iconHeight?: number;
  mobileIconWidth?: number;
  mobileIconHeight?: number;
  iconColor?: string;
}

const FAQModalAccordion: React.FC<ModalWithAccordionProps> = ({
  showFAQIcon,
  title,
  sections = [],
  iconWidth = 55,
  iconHeight = 55,
  mobileIconWidth = 30,
  mobileIconHeight = 30,
  iconColor = "#F609FF",
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { trackSelectionOption } = useTrackContext();
  const [accordionStates, setAccordionStates] = useState<boolean[]>(Array.isArray(sections) ? sections.map(() => false) : []);

  const windowWidth = window.innerWidth;

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isModalOpen]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleAccordion = (index: number) => {
    setAccordionStates((prevStates) => {
      const newState = prevStates.map((state, i) => (i === index ? !state : false));
      return newState;
    });
  };

  // Early return if sections is not an array or is empty
  if (!Array.isArray(sections) || sections.length === 0) {
    return null;
  }

  return (
    <>
      <div>
        <div className={`tw-text-white tw-underline tw-cursor-pointer tw-w-full md:tw-max-w-[550px]`} onClick={() => toggleModal()}>
          {
            showFAQIcon ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={windowWidth <= 768 ? mobileIconWidth : iconWidth}
                  height={windowWidth <= 768 ? mobileIconHeight : iconHeight}
                  viewBox="0 0 58 57"
                  fill={iconColor}
                >
                  <path
                    d="M57.4485 28.5001C57.4485 44.215 44.7122 56.9485 29.0001 56.9485C13.2881 56.9485 0.551758 44.215 0.551758 28.5001C0.551758 12.7909 13.2881 0.0517578 29.0001 0.0517578C44.7122 0.0517578 57.4485 12.7909 57.4485 28.5001Z"
                    fill={iconColor}
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M30.9567 27.0478C30.9567 25.9256 30.0729 25.0105 28.9891 25.0105C27.9052 25.0105 27.0215 25.9256 27.0215 27.0478V37.3392C27.0215 38.4614 27.9052 39.3765 28.9891 39.3765C30.0729 39.3765 30.9567 38.4614 30.9567 37.3392V27.0478ZM27.0326 19.6603C27.0326 20.7826 27.9118 21.6976 28.9889 21.6976C30.0975 21.6976 30.979 20.7826 30.979 19.6603C30.979 18.5381 30.0975 17.623 29.0114 17.623C27.9208 17.623 27.0326 18.5381 27.0326 19.6603Z"
                    fill={iconColor === "#F609FF" ? "white" : iconColor}
                  />
                </svg>
              </>
            ) : null
            // <span className="tw-mt-[20px] lg:tw-mt-[0]">Need help with your booking?</span>
          }
        </div>
      </div>
      {isModalOpen && (
        <div className="tw-fixed tw-z-[1000] tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-900 tw-bg-opacity-50 tw-p-[10px] lg:tw-p-0">
          <div
            className={`tw-bg-white tw-max-w-[998px] tw-bg-white tw-max-h-[80vh] tw-overflow-y-scroll tw-h-fit tw-w-full tw-mx-auto tw-rounded-[25px] tw-shadow-lg tw-transform tw-transition-all tw-duration-300 tw-scale-100`}
          >
            <div className="tw-px-[10px] tw-pb-[20px] lg:tw-px-[40px]" id="hk-accordion-contents">
              <div className="tw-flex lg:tw-justify-center tw-items-center tw-relative tw-py-[20px]">
                <div className="tw-text-black tw-text-[20px] tw-font-[700]">
                  {title}
                  {" - "}
                  <span className="tw-bg-gradient-to-r tw-from-[#a852ff] tw-via-[#a852ff] tw-to-[#7886ff] tw-inline-block tw-text-transparent tw-bg-clip-text">
                    {trackSelectionOption.title === "MAIN_TRACK"
                      ? "Main Track"
                      : trackSelectionOption.title === "VIRTUAL_REALITY"
                      ? "Virtual Reality"
                      : trackSelectionOption.title === "JUNIOR_TRACK"
                      ? "Junior Track"
                      : "Mini Track"}
                  </span>
                </div>
                <div className="tw-absolute tw-right-0">
                  <button className="tw-text-gray-600 tw-text-lg tw-hover:text-gray-800 tw-focus:tw-outline-none" onClick={toggleModal}>
                    <AiOutlineClose className="tw-w-[30px] tw-h-[30px] tw-text-[#C2C0C3] tw-font-[400]" />
                  </button>
                </div>
              </div>
              <div className="tw-space-y-4">
                {sections.map((section, index) => (
                  <div key={index} className={`${accordionStates[index] ? "tw-bg-[#d1d5db] tw-rounded-[5px] tw-overflow-hidden" : "tw-bg-white"}`}>
                    <div
                      className="tw-w-full tw-flex tw-justify-between tw-items-center tw-bg-white tw-text-black tw-border-[1px] tw-border-[#C2C0C3] tw-py-[15px] tw-px-[20px] tw-rounded-[5px] focus:tw-outline-none hover:tw-cursor-pointer"
                      onClick={() => toggleAccordion(index)}
                    >
                      <span className="tw-flex tw-items-center tw-gap-[15px]">
                        <div className="tw-hidden lg:tw-block">
                          <AccordionTitleIcon />
                        </div>
                        <div className="tw-block lg:tw-hidden">
                          <AccordionTitleIcon width={section.title.length > 24 ? 22 : 22} height={section.title.length > 24 ? 22 : 25} />
                        </div>
                        <span className="tw-text-[14] tw-font-[600] tw-text-black tw-text-left">{section.title}</span>
                      </span>
                      <div className="tw-p-[2px] tw-rounded-full tw-bg-[#F4F4F4] tw-flex tw-justify-between tw-items-center">
                        <svg
                          className={`tw-w-[18px] tw-h-[18px] tw-transform tw-transition-transform tw-duration-200 ${accordionStates[index] ? "tw-rotate-180" : "tw-rotate-0"}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                    {accordionStates[index] && (
                      <div
                        className="tw-bg-[#e2e8f0] tw-max-h-[300px] tw-h-full tw-overflow-auto tw-border-[1px] tw-border-[#C2C0C3] tw-p-[20px]"
                        dangerouslySetInnerHTML={{
                          __html: section.content as string,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={toggleModal}
                className="tw-mt-[10px] lg:tw-mb-0 tw-mx-auto tw-flex tw-justify-center tw-items-center tw-w-fit tw-h-fit tw-py-[14px] tw-px-[40px] tw-font-bold tw-text-[14px] tw-rounded-[100px] tw-text-white tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FAQModalAccordion;
